import { type FC, useCallback, useMemo } from "react";
import notification from "@app/services/notification";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import type { FinishedGoodsReceiptContent } from "../../../../../../api/finishedGoodsReceipts";
import { resolveStockFinishedGoodsReceiptContentScan } from "../../helpers/resolveStockFinishedGoodsReceiptContentScan";

const CONTENT_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-finished-goods-receipt.card.scan-content",
    title: "Сканируйте номер партии на этикетке изделия",
    description: "Найдём строку ВЗН УП по партии и откроем форму размещения продукции.",
    position: { placement: "bottom-left" },
    indicator: { variant: "scanner" },
};

/** Пропсы компонента {@link StockFinishedGoodsReceiptScanPlaceholder}. */
interface StockFinishedGoodsReceiptScanPlaceholderProps {
    /** Строки текущей ВЗН УП. */
    contents: FinishedGoodsReceiptContent[];
    /** Включён ли сценарий сканирования номера движения. */
    enabled: boolean;
    /**
     * Открывает форму размещения найденной строки.
     * @param contentCode код строки ВЗН УП
     */
    onOpenContent: (contentCode: number) => void;
}

/** Регистрирует сценарий сканирования этикетки изделия в карточке приемки СГП. */
export const StockFinishedGoodsReceiptScanPlaceholder: FC<StockFinishedGoodsReceiptScanPlaceholderProps> = ({
    contents,
    enabled,
    onOpenContent,
}) => {
    const handleScan = useCallback(
        async (barcode: string): Promise<void> => {
            const result = await resolveStockFinishedGoodsReceiptContentScan(barcode, contents);

            if (result.type === "notFound") {
                notification.warning(`В ВЗН УП не найдена строка для этикетки "${barcode}".`);
                return;
            }

            if (result.type === "ambiguous") {
                notification.warning(`Для этикетки "${barcode}" найдено несколько строк. Откройте строку вручную.`);
                return;
            }

            if (result.type === "alreadyPlaced") {
                notification.warning("Строка ВЗН УП уже полностью размещена.");
                onOpenContent(result.contentCode);
                return;
            }

            onOpenContent(result.contentCode);
        },
        [contents, onOpenContent],
    );

    const scannerRegistration = useMemo(
        (): ScannerRegistrationConfig => ({
            enabled,
            priority: "screen",
            captureInEditable: false,
            hint: CONTENT_SCAN_HINT,
            onScan: handleScan,
            errorSource: "stock.finishedGoodsReceipt.card.scanContent",
        }),
        [enabled, handleScan],
    );

    useScannerRegistration(scannerRegistration);

    return null;
};
